import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { SiteHealth, SiteHealthStatus } from '../data/site-health';

/**
 * Compact site-health pill used in lists + detail headers.
 *
 * <p>Renders one of three shapes driven by {@code variant()}:
 * <ul>
 *   <li>{@code dot}: 8px coloured dot only — fits inside a tight cell, no
 *       text. Use for the table view where space is at a premium.</li>
 *   <li>{@code badge}: dot + ratio (e.g. "4/4 ON") + status word — the
 *       default. Used on cards and the overview header.</li>
 *   <li>{@code card}: full panel with a breakdown of online/emitting/auto-sync
 *       counts. Used on the site-detail overview tab.</li>
 * </ul>
 *
 * <p>Tooltip carries the full breakdown in dot/badge variants — the user
 * hovers to get the same info the {@code card} variant shows inline.
 */
@Component({
  selector: 'lib-site-health-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule],
  template: `
    @switch (variant()) {
      @case ('dot') {
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold"
          [class]="pillClass()"
          [title]="tooltipText()">
          <span class="size-2 rounded-full" [class]="dotClass()"></span>
          {{ ratio() }}
        </span>
      }
      @case ('card') {
        <div class="flex flex-col gap-2 rounded-xl border p-4" [class]="cardClass()">
          <div class="flex items-center gap-2">
            <span class="size-2.5 rounded-full" [class]="dotClass()"></span>
            <span class="text-sm font-bold uppercase tracking-wider" [class]="textClass()">
              {{ 'siteHealth.status.' + health().status | transloco }}
            </span>
          </div>
          <div class="flex flex-col gap-1 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-gray-600 dark:text-slate-300">{{ 'siteHealth.metrics.online' | transloco }}</span>
              <span
                class="font-semibold"
                [class.text-green-700]="health().online === health().expected"
                [class.dark:text-green-400]="health().online === health().expected">
                {{ health().online }} / {{ health().expected || health().total }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-600 dark:text-slate-300">{{ 'siteHealth.metrics.emitting' | transloco }}</span>
              <span
                class="font-semibold"
                [class.text-green-700]="health().emitting === health().expected"
                [class.dark:text-green-400]="health().emitting === health().expected">
                {{ health().emitting }} / {{ health().expected || health().total }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-600 dark:text-slate-300">{{ 'siteHealth.metrics.autoSync' | transloco }}</span>
              <span
                class="font-semibold"
                [class.text-green-700]="health().autoSynced === health().expected"
                [class.dark:text-green-400]="health().autoSynced === health().expected">
                {{ health().autoSynced }} / {{ health().expected || health().total }}
              </span>
            </div>
            @if (health().total !== health().expected && health().expected > 0) {
              <div
                class="mt-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                {{ 'siteHealth.gatewayMismatch' | transloco: { actual: health().total, expected: health().expected } }}
              </div>
            }
          </div>
        </div>
      }
      @default {
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider"
          [class]="pillClass()"
          [title]="tooltipText()">
          <span class="size-2 rounded-full" [class]="dotClass()"></span>
          <span>{{ ratio() }}</span>
          <span class="opacity-70">·</span>
          <span>{{ 'siteHealth.statusShort.' + health().status | transloco }}</span>
        </span>
      }
    }
  `,
})
export class SiteHealthBadgeComponent {
  health = input.required<SiteHealth>();
  variant = input<'dot' | 'badge' | 'card'>('badge');

  protected readonly ratio = computed(() => {
    const h = this.health();
    const denom = h.expected > 0 ? h.expected : h.total;
    return `${h.online}/${denom || '?'}`;
  });

  protected readonly tooltipText = computed(() => {
    const h = this.health();
    const parts = [
      `Online: ${h.online}/${h.expected || h.total}`,
      `Emitting: ${h.emitting}/${h.expected || h.total}`,
      `Auto-sync: ${h.autoSynced}/${h.expected || h.total}`,
    ];
    if (h.total !== h.expected && h.expected > 0) parts.push(`(${h.total} attached, ${h.expected} expected)`);
    return parts.join(' · ');
  });

  protected readonly dotClass = computed(() => toneFor(this.health().status, 'dot'));
  protected readonly pillClass = computed(() => toneFor(this.health().status, 'pill'));
  protected readonly textClass = computed(() => toneFor(this.health().status, 'text'));
  protected readonly cardClass = computed(() => toneFor(this.health().status, 'card'));
}

function toneFor(status: SiteHealthStatus, slot: 'dot' | 'pill' | 'text' | 'card'): string {
  switch (status) {
    case 'OPERATIONAL':
      return {
        dot: 'bg-green-500',
        pill: 'bg-green-50 text-green-800 dark:bg-green-500/15 dark:text-green-300',
        text: 'text-green-800 dark:text-green-300',
        card: 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10',
      }[slot];
    case 'DEGRADED':
      return {
        dot: 'bg-amber-500',
        pill: 'bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
        text: 'text-amber-800 dark:text-amber-300',
        card: 'border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10',
      }[slot];
    case 'DOWN':
      return {
        dot: 'bg-red-500',
        pill: 'bg-red-50 text-red-800 dark:bg-red-500/15 dark:text-red-300',
        text: 'text-red-800 dark:text-red-300',
        card: 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10',
      }[slot];
    default:
      return {
        dot: 'bg-gray-400 dark:bg-slate-500',
        pill: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
        text: 'text-gray-700 dark:text-slate-300',
        card: 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/40',
      }[slot];
  }
}
